import { findOpenFilterItems } from '@aibees/elephant/feature/search-filters';
import {
  useSearchQueryFilter,
  useSearchRouteInfo,
  useTrackEvents,
} from '@aibees/elephant/hooks';
import { classnames } from '@aibees/shared/utils';
import { Box } from '@mui/material';
import React, { memo, useMemo, useState } from 'react';
import { FilterSection } from '../../components/filter-section';
import {
  getFieldsByFilterItems,
  getFilteringFields,
} from '../../components/filter-tags/utils';
import { useFilterSections } from '../../hooks/use-filter-sections';
import type { onChangeFilterGroupItem } from '../../types';
import { RadioGroupFilterItem } from './components/radio-group-filter-item';
import type { RadioGroupFilterProps as Props } from './types';
import { buildSearchParamsDraft, findCheckedRadio } from './utils';

export const RadioGroupFilter = memo(({ className, ...filter }: Props) => {
  const { type: searchType, table } = useSearchRouteInfo();
  const fields = getFieldsByFilterItems(filter.items!);
  const selectKeys = useMemo(
    () => filter.items?.map((i) => i.field!).filter(Boolean) ?? [],
    [filter.items]
  );
  const { params, setParams } = useSearchQueryFilter({ select: selectKeys });
  const { track } = useTrackEvents();
  const defaultOpen = findOpenFilterItems(filter)?.at(0) ?? undefined;
  const [open, setOpen] = useState<string | undefined>(defaultOpen);
  const filteringFields = getFilteringFields(table, params, filter);

  const { mainItems, moreItems } = useFilterSections(filter, filter.sections);

  const checkedRadio = findCheckedRadio(
    [...(mainItems ?? []), ...(moreItems ?? [])],
    filteringFields,
    open
  );

  const isAnyOfMoreItemsChecked = useMemo(() => {
    for (const item of moreItems ?? []) {
      if (item.field === checkedRadio) return true;
    }
  }, [moreItems, checkedRadio]);

  const handleChange: onChangeFilterGroupItem = (
    event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean,
    field,
    type
  ) => {
    setOpen(event.target.name);
    const draft = buildSearchParamsDraft(fields, field, type);
    track('FILTER', {
      filterName: field,
      filterType: filter.type,
      searchType,
    });
    setParams(draft);
  };

  return (
    <Box
      className={classnames('RadioGroupFilter', className)}
      display="flex"
      flexDirection="column"
      gap={1}
    >
      {mainItems?.map((item) => {
        const { key, open: o, ...props } = item;
        return (
          <RadioGroupFilterItem
            onChange={handleChange}
            key={key}
            open={item.field === checkedRadio}
            {...props}
          />
        );
      })}
      {moreItems?.length ? (
        <FilterSection title="More Filters" open={isAnyOfMoreItemsChecked}>
          {moreItems?.map((item) => {
            const { key, open: o, ...props } = item;
            return (
              <RadioGroupFilterItem
                onChange={handleChange}
                key={key}
                open={item.field === checkedRadio}
                {...props}
              />
            );
          })}
        </FilterSection>
      ) : null}
    </Box>
  );
});

RadioGroupFilter.displayName = 'RadioGroupFilter';
